<template>
  <div class="filter-group">
    <div class="filter-group-header">
      <span class="filter-group-title">
        Match patients who meet <strong>ALL</strong> of the following:
      </span>
      <button
        v-if="removable"
        class="remove-group-btn"
        title="Remove this group"
        @click="$emit('remove')"
      >
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div class="filter-group-body">
      <template v-for="(filter, index) in group.filters">
        <!-- AND Connector (between filter cards) -->
        <div v-if="index > 0" :key="'and-' + filter.id" class="and-connector">
          <span class="and-pill">AND</span>
        </div>

        <!-- Filter Card -->
        <ProcedureFilterCard
          v-if="filter.filterType === 'procedure'"
          :key="'filter-' + filter.id"
          :value="filter"
          :proc-code-options="procCodeOptions"
          @input="updateFilter(index, $event)"
          @remove="removeFilter(index)"
          @duplicate="duplicateFilter(index)"
        />
        <AppointmentFilterCard
          v-else-if="filter.filterType === 'appointment'"
          :key="'filter-' + filter.id"
          :value="filter"
          @input="updateFilter(index, $event)"
          @remove="removeFilter(index)"
          @duplicate="duplicateFilter(index)"
        />
        <InsuranceFilterCard
          v-else-if="filter.filterType === 'insurance'"
          :key="'filter-' + filter.id"
          :value="filter"
          :insurance-options="insuranceOptions"
          @input="updateFilter(index, $event)"
          @remove="removeFilter(index)"
          @duplicate="duplicateFilter(index)"
        />
        <RecallFilterCard
          v-else-if="filter.filterType === 'recall'"
          :key="'filter-' + filter.id"
          :value="filter"
          :provider-options="providerOptions"
          :recall-type-options="recallTypeOptions"
          @input="updateFilter(index, $event)"
          @remove="removeFilter(index)"
          @duplicate="duplicateFilter(index)"
        />
        <DemographicFilterCard
          v-else-if="filter.filterType === 'demographic'"
          :key="'filter-' + filter.id"
          :value="filter"
          :zip-code-options="zipCodeOptions"
          @input="updateFilter(index, $event)"
          @remove="removeFilter(index)"
          @duplicate="duplicateFilter(index)"
        />
      </template>

      <!-- Empty state -->
      <div v-if="group.filters.length === 0" class="empty-group-msg">
        Add a filter to get started
      </div>

      <!-- Add Filter Dropdown -->
      <div class="add-filter-section">
        <b-dropdown variant="outline-primary" size="sm" text="+ Add Filter" class="add-filter-dropdown">
          <b-dropdown-item @click="addFilter('procedure')">
            <i class="fa fa-medkit mr-2 text-primary"></i>Procedure Filter
          </b-dropdown-item>
          <b-dropdown-item @click="addFilter('appointment')">
            <i class="fa fa-calendar mr-2 text-warning"></i>Appointment Filter
          </b-dropdown-item>
          <b-dropdown-item @click="addFilter('insurance')">
            <i class="fa fa-shield mr-2 text-info"></i>Insurance Filter
          </b-dropdown-item>
          <b-dropdown-item @click="addFilter('recall')">
            <i class="fa fa-refresh mr-2" style="color: #10b981;"></i>Recall Due
          </b-dropdown-item>
          <b-dropdown-item @click="addFilter('demographic')">
            <i class="fa fa-user mr-2 text-success"></i>Patient Demographic
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
import ProcedureFilterCard from './ProcedureFilterCard.vue';
import AppointmentFilterCard from './AppointmentFilterCard.vue';
import InsuranceFilterCard from './InsuranceFilterCard.vue';
import DemographicFilterCard from './DemographicFilterCard.vue';
import RecallFilterCard from './RecallFilterCard.vue';

export default {
  name: 'FilterGroup',
  components: { ProcedureFilterCard, AppointmentFilterCard, InsuranceFilterCard, DemographicFilterCard, RecallFilterCard },
  props: {
    value: {
      type: Object,
      required: true
    },
    procCodeOptions: {
      type: Array,
      default: () => []
    },
    zipCodeOptions: {
      type: Array,
      default: () => []
    },
    insuranceOptions: {
      type: Object,
      default: () => ({ carriers: [], employers: [], groupNumbers: [] })
    },
    providerOptions: {
      type: Array,
      default: () => []
    },
    recallTypeOptions: {
      type: Array,
      default: () => []
    },
    removable: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      nextFilterId: this.value.filters.length > 0
        ? Math.max(...this.value.filters.map(f => f.id)) + 1
        : 1
    };
  },
  computed: {
    group() {
      return this.value;
    }
  },
  methods: {
    addFilter(filterType) {
      const id = this.nextFilterId++;
      let newFilter;

      if (filterType === 'procedure') {
        newFilter = {
          id,
          filterType: 'procedure',
          mode: 'has',
          selectionMode: 'specific',
          procCodes: [],
          statuses: [],
          timeframe: { value: 6, unit: 'months' },
          fee: null
        };
      } else if (filterType === 'appointment') {
        newFilter = {
          id,
          filterType: 'appointment',
          mode: 'none',
          statuses: [1],
          timeframe: { direction: 'next', value: 12, unit: 'months' }
        };
      } else if (filterType === 'insurance') {
        newFilter = {
          id,
          filterType: 'insurance',
          carriers: [],
          employers: [],
          groupNumbers: []
        };
      } else if (filterType === 'recall') {
        newFilter = {
          id,
          filterType: 'recall',
          timeframe: { mode: 'custom', from: '', to: '' },
          providers: [],
          recallTypes: []
        };
      } else if (filterType === 'demographic') {
        newFilter = {
          id,
          filterType: 'demographic',
          ageMin: null,
          ageMax: null,
          gender: 'all',
          zipCodes: []
        };
      }

      const updated = {
        ...this.group,
        filters: [...this.group.filters, newFilter]
      };
      this.$emit('input', updated);
    },
    updateFilter(index, updatedFilter) {
      const filters = [...this.group.filters];
      filters[index] = { ...filters[index], ...updatedFilter };
      this.$emit('input', { ...this.group, filters });
    },
    removeFilter(index) {
      const filters = this.group.filters.filter((_, i) => i !== index);
      this.$emit('input', { ...this.group, filters });
    },
    duplicateFilter(index) {
      const id = this.nextFilterId++;
      const clone = JSON.parse(JSON.stringify(this.group.filters[index]));
      clone.id = id;
      const filters = [...this.group.filters];
      filters.splice(index + 1, 0, clone);
      this.$emit('input', { ...this.group, filters });
    }
  }
};
</script>

<style scoped lang="scss">
.filter-group {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fafbfc;
  overflow: visible;
}

.filter-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 0.875rem;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 10px 10px 0 0;
}

.filter-group-title {
  font-size: 0.8125rem;
  color: #475569;
}

.remove-group-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.125rem 0.375rem;
  font-size: 0.875rem;

  &:hover {
    color: #ef4444;
  }
}

.filter-group-body {
  padding: 0.875rem;
}

.and-connector {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.625rem 0;
}

.and-pill {
  display: inline-block;
  padding: 0.1875rem 0.875rem;
  background: #eef2ff;
  color: #547fff;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.empty-group-msg {
  text-align: center;
  padding: 1.5rem;
  color: #94a3b8;
  font-size: 0.875rem;
}

.add-filter-section {
  margin-top: 0.75rem;
}

.add-filter-dropdown ::v-deep {
  .btn-outline-primary {
    font-size: 0.8125rem;
    border-color: #e2e8f0;
    color: #547fff;

    &:hover {
      background: #eef2ff;
      border-color: #547fff;
    }
  }
}
</style>
